import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import heroBeachBg from "../../Assests/images/hero-beach-bg.jpg";

const DEFAULT_POPULAR_CITIES = [
  { id: 1, name: "Sydney", country_name: "Australia" },
  { id: 2, name: "Melbourne", country_name: "Australia" },
  { id: 3, name: "Cairns", country_name: "Australia" },
  { id: 4, name: "Whitsundays", country_name: "Australia" },
  { id: 5, name: "Gold Coast", country_name: "Australia" },
  { id: 6, name: "Jaisalmer", country_name: "India" },
  { id: 7, name: "Varanasi", country_name: "India" },
];

function HeroV1({ onSearch }) {
  const [location, setLocation] = useState("");
  const [cities, setCities] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [counts, setCounts] = useState({ adults: 1, children: 0, infants: 0 });
  const [activeDropdown, setActiveDropdown] = useState(null); // 'where' | 'when' | 'guests' | null

  const searchFormRef = useRef(null);
  const locationInputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch real cities from backend API
  useEffect(() => {
    let isMounted = true;
    const fetchCities = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/getCities`
        );
        if (isMounted && res.data && Array.isArray(res.data.data)) {
          setCities(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching cities for Hero search:", err);
      }
    };
    fetchCities();
    return () => {
      isMounted = false;
    };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (searchFormRef.current && !searchFormRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Filter cities for autocomplete
  const filteredCities = useMemo(() => {
    const q = (location || "").trim().toLowerCase();
    const sourceList = cities.length > 0 ? cities : DEFAULT_POPULAR_CITIES;

    const uniqueMap = new Map();
    sourceList.forEach((c) => {
      if (c?.name && !uniqueMap.has(c.name.toLowerCase())) {
        uniqueMap.set(c.name.toLowerCase(), c);
      }
    });
    const uniqueList = Array.from(uniqueMap.values());

    if (!q) {
      return uniqueList.slice(0, 8);
    }

    const matches = uniqueList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.country_name && c.country_name.toLowerCase().includes(q))
    );

    if (q && !matches.some((m) => m.name.toLowerCase() === q)) {
      return [{ id: "custom", name: location.trim(), isTyped: true }, ...matches];
    }

    return matches;
  }, [cities, location]);

  const formatDateForApi = (date) => {
    if (!date) return "";
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().split("T")[0];
  };

  const formatDateDisplay = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formattedDateRange = useMemo(() => {
    if (startDate && endDate) {
      if (startDate.getTime() === endDate.getTime()) {
        return formatDateDisplay(startDate);
      }
      return `${formatDateDisplay(startDate)} – ${formatDateDisplay(endDate)}`;
    }
    if (startDate) {
      return formatDateDisplay(startDate);
    }
    return "";
  }, [startDate, endDate]);

  const formattedGuests = useMemo(() => {
    const total = counts.adults + counts.children;
    if (total === 0 && counts.infants === 0) return "";
    const guestStr = total > 0 ? `${total} ${total === 1 ? "guest" : "guests"}` : "";
    const infantStr =
      counts.infants > 0
        ? `${counts.infants} ${counts.infants === 1 ? "infant" : "infants"}`
        : "";
    if (guestStr && infantStr) return `${guestStr}, ${infantStr}`;
    return guestStr || infantStr;
  }, [counts]);

  // Date Shortcuts
  const handleDateShortcut = (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (type === "today") {
      setStartDate(today);
      setEndDate(today);
    } else if (type === "tomorrow") {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      setStartDate(tomorrow);
      setEndDate(tomorrow);
    } else if (type === "weekend") {
      const day = today.getDay();
      const diffToFriday = (5 - day + 7) % 7;
      const friday = new Date(today);
      friday.setDate(friday.getDate() + (diffToFriday === 0 && day === 5 ? 0 : diffToFriday));
      const sunday = new Date(friday);
      sunday.setDate(friday.getDate() + 2);
      setStartDate(friday);
      setEndDate(sunday);
    }
    setActiveDropdown("guests");
  };

  // Guest Steppers
  const handleGuestChange = (type, delta) => {
    setCounts((prev) => {
      const current = prev[type] || 0;
      const next = current + delta;
      if (next < 0) return prev;
      if (type === "adults" && next < 1 && (prev.children > 0 || prev.infants > 0)) {
        return prev; // at least 1 adult required if children present
      }
      if (type === "adults" && next > 16) return prev;
      if (type === "children" && next > 10) return prev;
      if (type === "infants" && next > 5) return prev;
      return { ...prev, [type]: next };
    });
  };

  // Selection handlers
  const handleSelectCity = (cityName) => {
    setLocation(cityName);
    setActiveDropdown("when");
  };

  // Search submission
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setActiveDropdown(null);

    const totalGuests = counts.adults + counts.children + counts.infants;
    const arriveStr = formatDateForApi(startDate);
    const departStr = formatDateForApi(endDate);
    const locationStr = location.trim();

    if (onSearch) {
      onSearch({
        name: locationStr,
        arrive: arriveStr,
        depart: departStr,
        guest: totalGuests,
        adults: counts.adults,
        children: counts.children,
        infants: counts.infants,
      });
    } else {
      const queryParams = new URLSearchParams();
      if (locationStr) queryParams.set("location_search", locationStr);
      if (arriveStr) queryParams.set("arrive", arriveStr);
      if (departStr) queryParams.set("depart", departStr);
      if (totalGuests > 0) queryParams.set("guest", totalGuests);
      if (counts.children > 0) queryParams.set("child", counts.children);
      if (counts.infants > 0) queryParams.set("infants", counts.infants);
      queryParams.set("page", "1");
      queryParams.set("view", "list");

      navigate(`/experiences?${queryParams.toString()}`);
    }
  };

  return (
    <section
      className="v1-hero-section"
      style={{ backgroundImage: `url(${heroBeachBg})` }}
    >
      {/* Background Gradient Overlay */}
      <div className="v1-hero-gradient"></div>

      <div className="v1-hero-container">
        <div className="v1-hero-content">
          {/* Tagline */}
          <p className="v1-hero-tagline">EXPLORE · DISCOVER · BELONG</p>

          {/* Heading */}
          <h1 className="v1-hero-heading">
            A DESTINATION FOR
            <span className="v1-hero-heading-highlight">LIVED EXPERIENCES</span>
          </h1>

          {/* Subtitle */}
          <p className="v1-hero-subtitle">
            Discover and book unforgettable experiences with local experts around the world.
          </p>

          {/* Interactive Search Box Form */}
          <form
            className="v1-hero-search-form"
            onSubmit={handleSearchSubmit}
            ref={searchFormRef}
            autoComplete="off"
          >
            {/* WHERE */}
            <div
              className={`v1-search-col ${
                activeDropdown === "where" ? "v1-search-col-active" : ""
              }`}
              onClick={() => {
                setActiveDropdown("where");
                if (locationInputRef.current) locationInputRef.current.focus();
              }}
            >
              <svg
                className="v1-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <div className="v1-search-col-text">
                <span className="v1-search-col-label">Where</span>
                <input
                  ref={locationInputRef}
                  type="text"
                  className="v1-search-col-input"
                  placeholder="Search destinations"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    setActiveDropdown("where");
                  }}
                  onFocus={() => setActiveDropdown("where")}
                />
              </div>

              {location && (
                <button
                  type="button"
                  className="v1-search-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocation("");
                  }}
                  title="Clear destination"
                >
                  ×
                </button>
              )}

              {/* Where Dropdown */}
              {activeDropdown === "where" && (
                <div
                  className="v1-search-dropdown-menu v1-where-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="v1-dropdown-header-label">
                    {location.trim() ? "MATCHING DESTINATIONS" : "POPULAR DESTINATIONS"}
                  </div>
                  <ul className="v1-dropdown-list">
                    {filteredCities.map((c) => (
                      <li
                        key={c.id || c.name}
                        className="v1-dropdown-item"
                        onClick={() => handleSelectCity(c.name)}
                      >
                        <div className="v1-dropdown-item-icon">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                        </div>
                        <div className="v1-dropdown-item-info">
                          <span className="v1-dropdown-city-title">
                            {c.isTyped ? `Search for "${c.name}"` : c.name}
                          </span>
                          {c.country_name && (
                            <span className="v1-dropdown-city-sub">
                              {c.country_name}
                            </span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* WHEN */}
            <div
              className={`v1-search-col ${
                activeDropdown === "when" ? "v1-search-col-active" : ""
              }`}
              onClick={() =>
                setActiveDropdown(activeDropdown === "when" ? null : "when")
              }
            >
              <svg
                className="v1-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <div className="v1-search-col-text">
                <span className="v1-search-col-label">When</span>
                <input
                  type="text"
                  className="v1-search-col-input"
                  placeholder="Select dates"
                  value={formattedDateRange}
                  readOnly
                />
              </div>

              {(startDate || endDate) && (
                <button
                  type="button"
                  className="v1-search-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStartDate(null);
                    setEndDate(null);
                  }}
                  title="Clear dates"
                >
                  ×
                </button>
              )}

              {/* When Datepicker Dropdown */}
              {activeDropdown === "when" && (
                <div
                  className="v1-search-dropdown-menu v1-when-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="v1-date-shortcuts">
                    <button
                      type="button"
                      className="v1-date-shortcut-btn"
                      onClick={() => handleDateShortcut("today")}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className="v1-date-shortcut-btn"
                      onClick={() => handleDateShortcut("tomorrow")}
                    >
                      Tomorrow
                    </button>
                    <button
                      type="button"
                      className="v1-date-shortcut-btn"
                      onClick={() => handleDateShortcut("weekend")}
                    >
                      This weekend
                    </button>
                  </div>
                  <div className="v1-datepicker-wrapper">
                    <DatePicker
                      selected={startDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setStartDate(start);
                        setEndDate(end);
                        if (start && end) {
                          setTimeout(() => setActiveDropdown("guests"), 300);
                        }
                      }}
                      startDate={startDate}
                      endDate={endDate}
                      minDate={new Date()}
                      selectsRange
                      inline
                      monthsShown={1}
                    />
                  </div>
                  <div className="v1-dropdown-actions">
                    <button
                      type="button"
                      className="v1-dropdown-clear-link"
                      onClick={() => {
                        setStartDate(null);
                        setEndDate(null);
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="v1-dropdown-done-btn"
                      onClick={() => setActiveDropdown("guests")}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* GUESTS */}
            <div
              className={`v1-search-col ${
                activeDropdown === "guests" ? "v1-search-col-active" : ""
              }`}
              onClick={() =>
                setActiveDropdown(activeDropdown === "guests" ? null : "guests")
              }
            >
              <svg
                className="v1-search-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <div className="v1-search-col-text">
                <span className="v1-search-col-label">Guests</span>
                <input
                  type="text"
                  className="v1-search-col-input"
                  placeholder="Add guests"
                  value={formattedGuests}
                  readOnly
                />
              </div>

              {counts.adults + counts.children + counts.infants > 1 && (
                <button
                  type="button"
                  className="v1-search-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCounts({ adults: 1, children: 0, infants: 0 });
                  }}
                  title="Reset guests"
                >
                  ×
                </button>
              )}

              {/* Guests Dropdown */}
              {activeDropdown === "guests" && (
                <div
                  className="v1-search-dropdown-menu v1-guests-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Adults */}
                  <div className="v1-guest-row">
                    <div className="v1-guest-info">
                      <span className="v1-guest-title">Adults</span>
                      <span className="v1-guest-subtitle">Ages 13 or above</span>
                    </div>
                    <div className="v1-guest-stepper">
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("adults", -1)}
                        disabled={counts.adults <= 1}
                      >
                        –
                      </button>
                      <span className="v1-stepper-val">{counts.adults}</span>
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("adults", 1)}
                        disabled={counts.adults >= 16}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="v1-guest-row">
                    <div className="v1-guest-info">
                      <span className="v1-guest-title">Children</span>
                      <span className="v1-guest-subtitle">Ages 2–12</span>
                    </div>
                    <div className="v1-guest-stepper">
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("children", -1)}
                        disabled={counts.children <= 0}
                      >
                        –
                      </button>
                      <span className="v1-stepper-val">{counts.children}</span>
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("children", 1)}
                        disabled={counts.children >= 10}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Infants */}
                  <div className="v1-guest-row">
                    <div className="v1-guest-info">
                      <span className="v1-guest-title">Infants</span>
                      <span className="v1-guest-subtitle">Under 2 years</span>
                    </div>
                    <div className="v1-guest-stepper">
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("infants", -1)}
                        disabled={counts.infants <= 0}
                      >
                        –
                      </button>
                      <span className="v1-stepper-val">{counts.infants}</span>
                      <button
                        type="button"
                        className="v1-stepper-btn"
                        onClick={() => handleGuestChange("infants", 1)}
                        disabled={counts.infants >= 5}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="v1-dropdown-actions">
                    <button
                      type="button"
                      className="v1-dropdown-clear-link"
                      onClick={() =>
                        setCounts({ adults: 1, children: 0, infants: 0 })
                      }
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="v1-dropdown-done-btn"
                      onClick={() => setActiveDropdown(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* SUBMIT BUTTON */}
            <button type="submit" className="v1-search-submit-btn">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <span>Search</span>
            </button>
          </form>

          {/* Trust Badges */}
          <ul className="v1-hero-badges">
            <li className="v1-hero-badge-item">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="#FFFFFF"
                stroke="#FFFFFF"
                strokeWidth="1"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span>Handpicked Experiences</span>
            </li>
            <li className="v1-hero-badge-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Trusted Local Experts</span>
            </li>
            <li className="v1-hero-badge-item">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              <span>Best Price Guarantee</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

export default HeroV1;

